/**
 * piece-trail — how the Piece screen's "How it got here" trail is grouped.
 *
 * Kept React-free and free of `@/lib/api` so the node test runner can import
 * it (anything reaching `import.meta.env` throws under `tsx --test`).
 *
 * The design groups the trail by **kind of document**, not by agent. An
 * earlier pass grouped by agent — eight groups — which the comp does not
 * show. The four groups below are the comp's, in the comp's order.
 */

export const TRAIL_GROUPS = ["research", "drafts", "checks", "art"] as const;

export type TrailGroup = (typeof TRAIL_GROUPS)[number];

export const TRAIL_GROUP_LABEL: Record<TrailGroup, string> = {
  research: "Research & sources",
  drafts: "Drafts",
  checks: "Checks & reports",
  art: "Art direction",
};

/** Keyed off the `page_type` values production actually stores. */
const GROUP_OF: Record<string, TrailGroup> = {
  // Research & sources
  source_library: "research",
  research_report: "research",
  reference_card: "research",
  deep_dive_candidates: "research",
  story_bible: "research",
  character_sheet: "research",
  // Drafts
  drafting_report: "drafts",
  // Checks & reports
  correction_report: "checks",
  fact_check_report: "checks",
  continuity_report: "checks",
  seo_report: "checks",
  publish_status: "checks",
  pipeline_log: "checks",
};

/**
 * An unrecognised page_type falls to "checks" rather than vanishing — a new
 * report is far likelier to be a check than to be research or art, and a
 * document the trail silently drops is the exact failure the trail exists to
 * fix.
 */
export function trailGroupFor(pageType: string): TrailGroup {
  return GROUP_OF[pageType] ?? "checks";
}

/** "fact_check_report" → "Fact check report" */
export function humanisePageType(pageType: string): string {
  const words = pageType.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
